import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchAdminStats, fetchAdminSales } from '../../services/adminApi';
import { MODULES, BILLABLE_MODULE_KEYS } from '../../../config/modules';

const EXPIRING_SOON_DAYS = 30;
const MODULE_LABELS_SHORT = { waste: 'Odpady', ppwr: 'PPWR' };
const SALES_PAGE_SIZE = 20;

const PLAN_LABELS = {
    demo: 'Demo', start: 'Start', business: 'Business',
    S: 'S', M: 'M', L: 'L',
};

const METHOD_LABELS = { invoice: 'Faktúra', cash: 'Hotovosť', stripe: 'Stripe' };

function getGreeting() {
    const h = new Date().getHours();
    if (h < 12) return 'Dobré ráno';
    if (h < 18) return 'Dobré popoludnie';
    return 'Dobrý večer';
}

const toDateStr = (d) => d.toISOString().slice(0, 10);

const QUARTER_ENDS = [2, 5, 8, 11];

const PRESETS = [
    {
        key: 'today', label: 'Dnes',
        range: () => { const t = toDateStr(new Date()); return [t, t]; },
    },
    {
        key: 'week', label: 'Týždeň',
        range: () => {
            const to = new Date();
            const from = new Date(to);
            from.setDate(from.getDate() - 6);
            return [toDateStr(from), toDateStr(to)];
        },
    },
    {
        key: 'month', label: 'Mesiac',
        range: () => {
            const now = new Date();
            return [toDateStr(new Date(now.getFullYear(), now.getMonth(), 1)), toDateStr(now)];
        },
    },
    ...([1, 2, 3, 4].map(q => ({
        key: `q${q}`, label: `Q${q}`,
        range: () => {
            const y = new Date().getFullYear();
            const startMonth = (q - 1) * 3;
            const endMonth = QUARTER_ENDS[q - 1];
            const endDay = new Date(y, endMonth + 1, 0).getDate();
            return [toDateStr(new Date(y, startMonth, 1)), toDateStr(new Date(y, endMonth, endDay))];
        },
    }))),
    {
        key: 'year', label: 'Rok',
        range: () => {
            const now = new Date();
            return [toDateStr(new Date(now.getFullYear(), 0, 1)), toDateStr(now)];
        },
    },
];

const STAT_CARDS = [
    { key: 'tenants',             label: 'Subjekty',           color: 'blue'   },
    { key: 'activeSubscriptions', label: 'Aktívne predplatné', color: 'green'  },
    { key: 'users',               label: 'Používatelia',       color: 'purple' },
    { key: 'failedPayments',      label: 'Neúspešné platby',   color: 'red'    },
];

const MODULE_FILTER_OPTIONS = [
    { value: '', label: 'Všetky balíky' },
    ...BILLABLE_MODULE_KEYS.map(module => ({ value: module, label: MODULES[module] })),
];

const formatAmount = (cents) => {
    if (!cents && cents !== 0) return '—';
    return `${(cents / 100).toFixed(2)} €`;
};

const formatDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}`;
};

const pluralRecords = (n) => {
    if (n === 1) return '1 záznam';
    if (n < 5) return `${n} záznamy`;
    return `${n} záznamov`;
};

export default function Dashboard() {
    const { data } = useSelector(state => state.users);
    const navigate = useNavigate();

    // ── Top stats filters ──────────────────────────────────────────────────────
    const [moduleFilter, setModuleFilter] = useState('');
    const [fromFilter, setFromFilter] = useState('');
    const [toFilter, setToFilter] = useState('');

    const [stats, setStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(true);
    const [statsError, setStatsError] = useState(null);

    // ── Sales filters ──────────────────────────────────────────────────────────
    const [salesModule, setSalesModule] = useState('');
    const [salesFrom, setSalesFrom] = useState('');
    const [salesTo, setSalesTo] = useState('');
    const [activePreset, setActivePreset] = useState(null);

    const [salesData, setSalesData] = useState(null);
    const [salesPage, setSalesPage] = useState(1);
    const [salesLoading, setSalesLoading] = useState(true);
    const [salesError, setSalesError] = useState(null);

    // ── Loaders ────────────────────────────────────────────────────────────────
    const loadStats = useCallback(async () => {
        setStatsLoading(true);
        setStatsError(null);
        try {
            const res = await fetchAdminStats({ module: moduleFilter, from: fromFilter, to: toFilter });
            setStats(res.data);
        } catch {
            setStatsError('Nepodarilo sa načítať štatistiky.');
        } finally {
            setStatsLoading(false);
        }
    }, [moduleFilter, fromFilter, toFilter]);

    const loadSales = useCallback(async () => {
        setSalesLoading(true);
        setSalesError(null);
        try {
            const res = await fetchAdminSales({
                module: salesModule || undefined,
                from: salesFrom || undefined,
                to: salesTo || undefined,
                page: salesPage,
                limit: SALES_PAGE_SIZE,
            });
            setSalesData(res.data);
        } catch {
            setSalesError('Nepodarilo sa načítať predaje.');
        } finally {
            setSalesLoading(false);
        }
    }, [salesModule, salesFrom, salesTo, salesPage]);

    useEffect(() => { loadStats(); }, [loadStats]);
    useEffect(() => { loadSales(); }, [loadSales]);

    // ── Handlers ───────────────────────────────────────────────────────────────
    const handleStatsReset = () => { setModuleFilter(''); setFromFilter(''); setToFilter(''); };

    const applyPreset = (preset) => {
        const [from, to] = preset.range();
        setSalesFrom(from);
        setSalesTo(to);
        setActivePreset(preset.key);
        setSalesPage(1);
    };

    const handleSalesFromChange = (val) => { setSalesFrom(val); setActivePreset(null); setSalesPage(1); };
    const handleSalesToChange   = (val) => { setSalesTo(val);   setActivePreset(null); setSalesPage(1); };
    const handleSalesModuleChange = (val) => { setSalesModule(val); setSalesPage(1); };

    const handleSalesReset = () => {
        setSalesModule('');
        setSalesFrom('');
        setSalesTo('');
        setActivePreset(null);
        setSalesPage(1);
    };

    const hasSalesFilters = salesModule || salesFrom || salesTo;
    const summary = salesData?.summary;
    const sales = salesData?.data ?? [];
    const salesTotal = salesData?.total ?? 0;
    const salesPages = salesData?.pages ?? 1;

    return (
        <div className="adm-page">
            <div className="adm-page-header">
                <h1 className="adm-page-title">{getGreeting()}, {data?.name}.</h1>
                <p className="adm-page-subtitle">Vitajte v administračnom paneli Envicloud.</p>
            </div>

            {/* ── Top stats filters ── */}
            <div className="adm-filters">
                <select className="adm-filter-select" value={moduleFilter} onChange={e => setModuleFilter(e.target.value)}>
                    {MODULE_FILTER_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>Balík: {opt.label}</option>
                    ))}
                </select>
                <input type="date" className="adm-filter-input" value={fromFilter} onChange={e => setFromFilter(e.target.value)} max={toFilter || undefined} />
                <input type="date" className="adm-filter-input" value={toFilter} onChange={e => setToFilter(e.target.value)} min={fromFilter || undefined} />
                {(moduleFilter || fromFilter || toFilter) && (
                    <button className="adm-page-btn" onClick={handleStatsReset}>Zrušiť filtre</button>
                )}
            </div>

            {statsError && <div className="adm-error" style={{ marginBottom: 20 }}>{statsError}</div>}

            {/* ── Stat cards ── */}
            <div className="adm-stat-grid">
                {STAT_CARDS.map(card => (
                    <div key={card.key} className="adm-stat-card">
                        <div className={`adm-stat-dot adm-stat-dot--${card.color}`} />
                        <div>
                            <p className="adm-stat-label">{card.label}</p>
                            <p className="adm-stat-value">
                                {statsLoading ? <span className="adm-spinner" /> : (stats?.[card.key] ?? '—')}
                            </p>
                        </div>
                    </div>
                ))}
            </div>

            {/* ── Expiring ── */}
            <div className="adm-section-title" style={{ marginTop: 32 }}>Expiruje do {EXPIRING_SOON_DAYS} dní</div>
            <div className="adm-stat-grid">
                {BILLABLE_MODULE_KEYS.map(module => (
                    <button
                        key={module}
                        className="adm-stat-card adm-stat-card--clickable"
                        onClick={() => navigate(`/admin/tenants?module=${module}&expiringDays=${EXPIRING_SOON_DAYS}`)}
                    >
                        <div className="adm-stat-dot adm-stat-dot--yellow" />
                        <div>
                            <p className="adm-stat-label">{MODULE_LABELS_SHORT[module] ?? MODULES[module]}</p>
                            <p className="adm-stat-value">
                                {statsLoading ? <span className="adm-spinner" /> : (stats?.expiringByModule?.[module] ?? '—')}
                            </p>
                        </div>
                    </button>
                ))}
            </div>

            {/* ── Sales section ── */}
            <div className="adm-section-title" style={{ marginTop: 32 }}>Prehľad predajov</div>

            <div className="adm-filters" style={{ flexWrap: 'wrap', gap: 8 }}>
                <div style={{ display: 'flex', gap: 4 }}>
                    {PRESETS.map(preset => (
                        <button
                            key={preset.key}
                            className={`adm-page-btn${activePreset === preset.key ? ' adm-page-btn--active' : ''}`}
                            onClick={() => applyPreset(preset)}
                        >
                            {preset.label}
                        </button>
                    ))}
                </div>
                <select className="adm-filter-select" value={salesModule} onChange={e => handleSalesModuleChange(e.target.value)}>
                    {MODULE_FILTER_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>Balík: {opt.label}</option>
                    ))}
                </select>
                <input type="date" className="adm-filter-input" value={salesFrom} onChange={e => handleSalesFromChange(e.target.value)} max={salesTo || undefined} />
                <input type="date" className="adm-filter-input" value={salesTo} onChange={e => handleSalesToChange(e.target.value)} min={salesFrom || undefined} />
                {hasSalesFilters && (
                    <button className="adm-page-btn" onClick={handleSalesReset}>Zrušiť</button>
                )}
            </div>

            {/* ── Sales summary cards (react to sales filters) ── */}
            <div className="adm-stat-grid" style={{ marginTop: 16 }}>
                <div className="adm-stat-card">
                    <div className="adm-stat-dot adm-stat-dot--green" />
                    <div>
                        <p className="adm-stat-label">Predané licencie</p>
                        <p className="adm-stat-value">
                            {salesLoading ? <span className="adm-spinner" /> : (summary?.count ?? '—')}
                        </p>
                    </div>
                </div>
                <div className="adm-stat-card">
                    <div className="adm-stat-dot adm-stat-dot--blue" />
                    <div>
                        <p className="adm-stat-label">Tržby</p>
                        <p className="adm-stat-value">
                            {salesLoading ? <span className="adm-spinner" /> : formatAmount(summary?.revenue)}
                        </p>
                    </div>
                </div>
                {BILLABLE_MODULE_KEYS.map(module => (
                    <div key={module} className="adm-stat-card">
                        <div className="adm-stat-dot adm-stat-dot--purple" />
                        <div>
                            <p className="adm-stat-label">{MODULE_LABELS_SHORT[module] ?? MODULES[module]}</p>
                            <p className="adm-stat-value">
                                {salesLoading
                                    ? <span className="adm-spinner" />
                                    : summary?.byModule?.[module]
                                        ? `${summary.byModule[module].count} / ${formatAmount(summary.byModule[module].revenue)}`
                                        : '—'
                                }
                            </p>
                        </div>
                    </div>
                ))}
            </div>

            {salesError && <div className="adm-error" style={{ marginTop: 12 }}>{salesError}</div>}

            {/* ── Sales table ── */}
            {salesLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}>
                    <span className="adm-spinner" />
                </div>
            ) : sales.length === 0 ? (
                <div className="adm-empty" style={{ marginTop: 16 }}>Žiadne predaje nevyhovujú filtrom.</div>
            ) : (
                <div className="adm-table-wrap" style={{ marginTop: 16 }}>
                    <table className="adm-table">
                        <thead>
                            <tr>
                                <th>Subjekt</th>
                                <th>Balík</th>
                                <th>Plán</th>
                                <th>Suma</th>
                                <th>Spôsob platby</th>
                                <th>Obdobie</th>
                                <th>Dátum</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sales.map(p => (
                                <tr key={p._id}>
                                    <td className="adm-td-primary">{p.tenantId?.companyName ?? '—'}</td>
                                    <td><span className="adm-badge adm-badge--gray">{MODULE_LABELS_SHORT[p.module] ?? p.module}</span></td>
                                    <td>{PLAN_LABELS[p.plan] ?? p.plan ?? '—'}</td>
                                    <td style={{ fontVariantNumeric: 'tabular-nums' }}>{formatAmount(p.amount)}</td>
                                    <td>{METHOD_LABELS[p.paymentMethod] ?? p.paymentMethod ?? '—'}</td>
                                    <td style={{ fontSize: 12, color: 'var(--adm-text-2)' }}>
                                        {p.periodStart || p.periodEnd
                                            ? `${formatDate(p.periodStart)} – ${formatDate(p.periodEnd)}`
                                            : '—'
                                        }
                                    </td>
                                    <td>{formatDate(p.createdAt)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className="adm-table-footer">
                        <span>{pluralRecords(salesTotal)}</span>
                        {salesPages > 1 && (
                            <div className="adm-pagination">
                                <button className="adm-page-btn" onClick={() => setSalesPage(p => p - 1)} disabled={salesPage === 1}>‹</button>
                                {Array.from({ length: salesPages }, (_, i) => i + 1).map(p => (
                                    <button
                                        key={p}
                                        className={`adm-page-btn${p === salesPage ? ' adm-page-btn--active' : ''}`}
                                        onClick={() => setSalesPage(p)}
                                    >
                                        {p}
                                    </button>
                                ))}
                                <button className="adm-page-btn" onClick={() => setSalesPage(p => p + 1)} disabled={salesPage === salesPages}>›</button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
